import { createElement, useEffect, useState } from 'react';

type CastState = 'loading' | 'ready' | 'unavailable';
type CastButtonVariant = 'toolbar' | 'landing';

interface CastButtonProps {
  variant?: CastButtonVariant;
}

interface CastWindow extends Window {
  __onGCastApiAvailable?: (isAvailable: boolean) => void;
  chrome?: {
    cast?: {
      AutoJoinPolicy: { ORIGIN_SCOPED: string };
      DefaultActionPolicy: { CAST_THIS_TAB: string };
      media: { DEFAULT_MEDIA_RECEIVER_APP_ID: string };
    };
  };
  cast?: {
    framework?: {
      CastContext: {
        getInstance: () => {
          requestSession?: () => Promise<unknown>;
          setOptions: (options: {
            receiverApplicationId: string;
            autoJoinPolicy: string;
            defaultActionPolicy: string;
          }) => void;
        };
      };
    };
  };
}

const CAST_SENDER_SRC = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';
let castSdkPromise: Promise<boolean> | null = null;
let castSdkConfigured = false;

function castWindow() {
  return window as CastWindow;
}

function configureCastContext() {
  if (castSdkConfigured) return true;
  const sdkWindow = castWindow();
  const castApi = sdkWindow.chrome?.cast;
  const castFramework = sdkWindow.cast?.framework;
  if (!castApi || !castFramework) return false;

  castFramework.CastContext.getInstance().setOptions({
    receiverApplicationId: castApi.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
    autoJoinPolicy: castApi.AutoJoinPolicy.ORIGIN_SCOPED,
    defaultActionPolicy: castApi.DefaultActionPolicy.CAST_THIS_TAB,
  });
  castSdkConfigured = true;
  return true;
}

function loadCastSdk(): Promise<boolean> {
  if (castSdkPromise) return castSdkPromise;

  castSdkPromise = new Promise((resolve) => {
    if (castWindow().chrome?.cast && castWindow().cast?.framework) {
      resolve(configureCastContext());
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${CAST_SENDER_SRC}"]`);
    const previousCallback = castWindow().__onGCastApiAvailable;
    castWindow().__onGCastApiAvailable = (isAvailable) => {
      previousCallback?.(isAvailable);
      resolve(Boolean(isAvailable && configureCastContext()));
    };

    if (existingScript) {
      window.setTimeout(() => resolve(configureCastContext()), 1500);
      return;
    }

    const script = document.createElement('script');
    script.src = CAST_SENDER_SRC;
    script.async = true;
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });

  return castSdkPromise;
}

async function requestCastSession() {
  try {
    const context = castWindow().cast?.framework?.CastContext.getInstance();
    await context?.requestSession?.();
  } catch {
    // Users can cancel the Cast picker; no UI state needs to change here.
  }
}

export default function CastButton({ variant = 'toolbar' }: CastButtonProps) {
  const [state, setState] = useState<CastState>('loading');

  useEffect(() => {
    let alive = true;
    loadCastSdk().then((ready) => {
      if (alive) setState(ready ? 'ready' : 'unavailable');
    });
    return () => {
      alive = false;
    };
  }, []);

  const unavailable = state !== 'ready';
  const title = state === 'loading'
    ? 'Loading Cast'
    : state === 'ready'
      ? 'Cast visualizer to a Chromecast'
      : 'Cast is available in Chrome with a Chromecast on this network';

  return (
    <span
      className={`party-cast-control party-cast-control--${variant} party-cast-control--${state}`}
      aria-disabled={unavailable}
      title={title}
      onClick={(event) => {
        if (unavailable) return;
        const tagName = (event.target as HTMLElement).tagName.toLowerCase();
        if (tagName !== 'google-cast-launcher') requestCastSession();
      }}
    >
      {state === 'ready' ? (
        <span className="party-cast-icon-shell">
          {/*
            The Cast SDK defines this custom element when it is available.
            It owns the native picker and connected-state icon.
          */}
          {createElement('google-cast-launcher', { className: 'party-cast-launcher' })}
        </span>
      ) : (
        <span className="party-cast-icon-shell party-cast-icon-shell--placeholder" aria-hidden="true">
          <span />
        </span>
      )}
      <span className="party-cast-copy">Cast</span>
    </span>
  );
}
